import { Spinner } from '@chakra-ui/react/spinner';
import { createRecipeContext, type HTMLChakraProps, type RecipeProps } from '@chakra-ui/react/styled-system';
import { Text } from '@chakra-ui/react/text';
import { forwardRef, type KeyboardEvent, type MouseEvent, type Ref } from 'react';

import { type BitkitIconComponent } from '../../icons';
import colorButtonRecipe from '../../theme/recipes/ColorButton.recipe';

type ColorButtonOmitted = 'as' | 'colorPalette' | 'size' | 'type';

type StyledButtonProps = Omit<HTMLChakraProps<'button'>, 'colorPalette' | 'size'> & RecipeProps<'colorButton'>;
type StyledAnchorProps = Omit<HTMLChakraProps<'a'>, 'colorPalette' | 'size'> & RecipeProps<'colorButton'>;

interface BitkitColorButtonCommonProps extends RecipeProps<'colorButton'> {
  /**
   * Disabled or loading state of the control. Both are honoured in anchor mode too.
   *
   * Note that `'loading'` behaves differently here than on `BitkitButton`, which delegates to
   * Chakra's `Button loading` and therefore also *disables* and dims the control. This one stays
   * focusable and undimmed: the label keeps its width, a spinner is overlaid on top of it, and
   * activation is blocked by a click guard — so a keyboard or screen reader user doesn't lose the
   * element they were on the moment they press it.
   */
  state?: 'disabled' | 'loading';
  suffixIcon?: BitkitIconComponent;
}

export interface BitkitColorButtonAsButtonProps
  extends BitkitColorButtonCommonProps, Omit<HTMLChakraProps<'button'>, ColorButtonOmitted | 'disabled'> {
  href?: undefined;
  isExternal?: undefined;
}

export interface BitkitColorButtonAsAnchorProps
  extends BitkitColorButtonCommonProps, Omit<HTMLChakraProps<'a'>, ColorButtonOmitted> {
  href: string;
  isExternal?: boolean;
}

export type BitkitColorButtonProps = BitkitColorButtonAsButtonProps | BitkitColorButtonAsAnchorProps;

const { withContext } = createRecipeContext({ recipe: colorButtonRecipe });

const StyledButton = withContext<HTMLButtonElement, StyledButtonProps>('button');

const StyledAnchor = withContext<HTMLAnchorElement, StyledAnchorProps>('a');

const BitkitColorButton = forwardRef<HTMLButtonElement | HTMLAnchorElement, BitkitColorButtonProps>((props, ref) => {
  const { children, size, state, suffixIcon: SuffixIcon } = props;
  const isDisabled = state === 'disabled';
  const isLoading = state === 'loading';
  const isInactive = isDisabled || isLoading;
  const spinnerSize = size === 'lg' ? '20' : '16';
  // Keep the fallback equal to `defaultVariants.size` in ColorButton.recipe.ts.
  const iconSize = (size ?? 'sm') === 'lg' ? '24' : '16';

  const content = (
    <>
      {isLoading ? (
        // `opacity: 0` rather than `visibility: hidden`: both reserve the label's width so the
        // button doesn't collapse, but `visibility: hidden` also drops it from the accessibility
        // tree — leaving a focusable control with no accessible name.
        <Text as="span" opacity="0" userSelect="none">
          {children}
        </Text>
      ) : (
        children
      )}
      {SuffixIcon && (
        <SuffixIcon marginInlineEnd="-4" marginInlineStart="8" opacity={isLoading ? 0 : undefined} size={iconSize} />
      )}
      {isLoading && (
        <Spinner
          position="absolute"
          insetBlock="0"
          insetInline="0"
          margin="auto"
          height={spinnerSize}
          width={spinnerSize}
        />
      )}
    </>
  );

  // --- Anchor mode (href) ---
  if (props.href !== undefined) {
    const {
      children: _children,
      href,
      isExternal,
      onClick,
      onKeyDown,
      rel,
      state: _state,
      suffixIcon: _suffixIcon,
      target,
      ...anchorRest
    } = props;
    const effectiveTarget = isExternal ? '_blank' : target;
    const effectiveRel = isExternal ? (rel ? `${rel} noreferrer noopener` : 'noreferrer noopener') : rel;
    const handleClick = isInactive
      ? (e: MouseEvent<HTMLAnchorElement>) => {
          e.preventDefault();
          e.stopPropagation();
        }
      : onClick;
    // What actually blocks keyboard activation in both states is `handleClick`: neither state
    // removes the anchor from the tab order (`loading` never sets `disabled`, and a disabled anchor
    // is only `aria-disabled`), and Enter/Space dispatch a click, which the guard above swallows.
    // The keydown guard is the extra layer an anchor needs, since it never gets the native
    // `disabled` treatment a button does.
    const handleKeyDown = isInactive
      ? (e: KeyboardEvent<HTMLAnchorElement>) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
          }
        }
      : onKeyDown;

    return (
      <StyledAnchor
        ref={ref as Ref<HTMLAnchorElement>}
        {...anchorRest}
        aria-disabled={isDisabled || undefined}
        data-disabled={isDisabled || undefined}
        // `data-loading` (not `aria-busy`) — it's the styling hook the recipe's `_loading` condition
        // reads, without claiming ARIA semantics that aren't announced anyway. Announcing the state
        // change to assistive tech needs a live region inside the component; that's its own change.
        data-loading={isLoading || undefined}
        // The `href` is kept while loading — so the link stays focusable and keeps its accessible
        // name, with activation still blocked by the guards above — and dropped only when disabled,
        // to take the anchor out of the tab order and make it fully inert.
        href={isDisabled ? undefined : href}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        rel={effectiveRel}
        target={effectiveTarget}
      >
        {content}
      </StyledAnchor>
    );
  }

  // --- Button mode (default) ---
  const { children: _children, onClick, state: _state, suffixIcon: _suffixIcon, ...buttonRest } = props;
  const handleClick = isInactive
    ? (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
      }
    : onClick;

  return (
    <StyledButton
      ref={ref as Ref<HTMLButtonElement>}
      {...buttonRest}
      data-loading={isLoading || undefined}
      disabled={isDisabled}
      onClick={handleClick}
      type="button"
    >
      {content}
    </StyledButton>
  );
});

BitkitColorButton.displayName = 'BitkitColorButton';

export default BitkitColorButton;
